import { z } from "zod";
import { twinMeasureBasisSchema } from "./twin-measure-basis.js";
const fieldName = z.string().regex(/^[a-z_][a-z0-9_]{0,126}$/u);
const text = z.string().trim().min(1).max(2_000);
const unique = (values) => new Set(values).size === values.length;
const semanticFields = {
    grain: z.array(fieldName).min(1).max(256).refine(unique, "Grain fields must be unique."),
    fields: z.array(z.object({
        field: fieldName,
        postgresType: z.string().trim().min(1).max(256),
        semanticRole: z.enum(["unknown", "dimension", "measure", "entity_key", "temporal", "status", "currency"]),
        aggregationPolicy: text,
        additivityPartition: z.union([
            z.array(fieldName).max(256).refine(unique, "Partition fields must be unique."),
            z.literal("unknown"),
        ]),
        temporalBasis: text,
        currencyBasis: text,
    }).strict()).min(1).max(256),
};
function requireFieldBindings(semantics, context) {
    const fields = new Set(semantics.fields.map(({ field }) => field));
    if (fields.size !== semantics.fields.length) {
        context.addIssue({ code: "custom", path: ["fields"], message: "Query semantic fields must be unique." });
    }
    for (const name of [...semantics.grain, ...semantics.fields.flatMap((field) => field.additivityPartition === "unknown" ? [] : field.additivityPartition)]) {
        if (!fields.has(name)) {
            context.addIssue({ code: "custom", message: `Query semantic field '${name}' is not declared.` });
        }
    }
}
const productSlug = z.string().regex(/^[a-z0-9](?:[a-z0-9-]*[a-z0-9])?$/u).max(80);
const count = z.number().int().nonnegative().max(Number.MAX_SAFE_INTEGER);
/**
 * A key this product holds to another product of its release: `fields[i]` holds values of
 * `productFields[i]`, the other product's grain. Twin Build measured it on the authored views:
 * `matchedValues` of the fields' `distinctValues` distinct values are key values there.
 */
export const twinProductKeySchema = z.object({
    fields: z.array(fieldName).min(1).max(3).refine(unique, "Key fields must be unique."),
    productSlug,
    productFields: z.array(fieldName).min(1).max(3).refine(unique, "Key fields must be unique."),
    distinctValues: count,
    matchedValues: count,
}).strict().superRefine((key, context) => {
    if (key.fields.length !== key.productFields.length) {
        context.addIssue({ code: "custom", message: "A key's fields must match the other product's key." });
    }
    if (key.matchedValues > key.distinctValues) {
        context.addIssue({ code: "custom", message: "A key cannot match more values than it holds." });
    }
});
/** Current executable semantics, produced by the compiler and verified at admission. */
export const twinQuerySemanticsSchema = z.object({
    schemaVersion: z.literal("mlx.twin-query-semantics.v2"),
    ...semanticFields,
    fields: semanticFields.fields.element.extend({
        measureBasis: twinMeasureBasisSchema,
        // A small category field's values, measured by Twin Build; absent for every other field.
        values: z.array(z.string().max(80)).min(1).max(20).optional(),
    }).array().min(1).max(256),
    // Absent when the product holds no key, so semantics authored before keys keep their bytes.
    keys: z.array(twinProductKeySchema).min(1).max(64).optional(),
}).strict().superRefine((semantics, context) => {
    requireFieldBindings(semantics, context);
    const fields = new Set(semantics.fields.map(({ field }) => field));
    for (const key of semantics.keys ?? []) {
        for (const field of key.fields) {
            if (!fields.has(field)) {
                context.addIssue({ code: "custom", message: `Key field '${field}' is not declared.` });
            }
        }
    }
});
/** Evidence-only historical bytes. This parser never upgrades an executable contract. */
const archivedV1 = z.object({
    schemaVersion: z.literal("mlx.twin-query-semantics.v1"), ...semanticFields,
}).strict().superRefine(requireFieldBindings);
export const archivedTwinQuerySemanticsSchema = z.union([twinQuerySemanticsSchema, archivedV1]);
export function currentQuerySemanticsAvailability(value) {
    return twinQuerySemanticsSchema.safeParse(value).success ? "current" : "reauthor_required";
}
export function requireCurrentExecutableQuerySemantics(value) {
    const result = twinQuerySemanticsSchema.safeParse(value);
    if (!result.success)
        throw new Error("twin_query_semantics_reauthor_required");
    return result.data;
}
